<template>
  <div class="row">
    <div class="col-12">
      <p class="lead">
        The chart below visualizes the future readiness performance of all 102
        cities - overall and by pillar. The list is in order of overall CFRI
        score.
      </p>
    </div>

    <div class="col-12 text-center animate__animated animate__fadeIn mb-5 mt-3">
      <p class="font-weight-500 help-text-green">
        <i class="fa fa-lightbulb-o mr-2" />Mouse over the bars to see each
        city's scores per pillar. Use the chart legend to toggle between
        readiness pillars
      </p>
    </div>

    <bar-chart
      :chartData="this.chartData"
      style="width: 100%"
      class="graph-view-bar-chart-2025"
    ></bar-chart>
  </div>
</template>
<script>
import BarChart from "@/components-2025/charts/BarChart";
import citiesData from "@/data_2025/cities.json";

export default {
  name: "GraphView",
  components: { BarChart },
  data() {
    return {
      citiesData: citiesData,

      mainPillars: [
        {
          key: "FREI",
          field: "future_readiness_score",
          label: "CFRI",
          visible: true,
          color: "#4285F4",
        },
        {
          key: "PeopleCentricity",
          field: "people_centricity_score",
          label: "People Centricity",
          visible: true,
          color: "#888888",
        },
        {
          key: "Openness",
          field: "openness_score",
          label: "Openness",
          visible: true,
          color: "#F4B400",
        },
        {
          key: "Sustainability",
          field: "sustainability_score",
          label: "Sustainability",
          visible: true,
          color: "#0F9D58",
        },
        {
          key: "InstitutionsGovernance",
          field: "institutions_governance_score",
          label: "Institutions & Governance",
          visible: false,
          color: "#9C27B0",
        },
        {
          key: "TransportInfrastructure",
          field: "transport_infrastructure_score",
          label: "Transport & Infrastructure",
          visible: false,
          color: "#009688",
        },
        {
          key: "Innovation",
          field: "innovation_score",
          label: "Innovation",
          visible: true,
          color: "#FF9800",
        },
        {
          key: "Vibrancy",
          field: "vibrancy_score",
          label: "Vibrancy",
          visible: false,
          color: "#E91E63",
        },
        {
          key: "EIntelligence",
          field: "e_intelligence_score",
          label: "E-Readiness",
          visible: true,
          color: "#3F51B5",
        },
      ],

      subPillars: [
        {
          key: "Liveability",
          field: "liveability_score",
          label: "Liveability",
          visible: false,
          color: "#00BCD4",
        },
        {
          key: "AccessToServices",
          field: "access_to_services_score",
          label: "Access to Services",
          visible: false,
          color: "#607D8B",
        },
        {
          key: "InvestmentOpenness",
          field: "investment_openness_score",
          label: "Investment Openness",
          visible: false,
          color: "#795548",
        },
        {
          key: "TalentCompetitiveness",
          field: "talent_competitiveness_score",
          label: "Talent Competitiveness",
          visible: false,
          color: "#FF5722",
        },
        {
          key: "UrbanDesign",
          field: "urban_design_score",
          label: "Urban Design",
          visible: false,
          color: "#8BC34A",
        },
        {
          key: "EnergyWaste",
          field: "energy_waste_score",
          label: "Energy and Waste",
          visible: false,
          color: "#CDDC39",
        },
        {
          key: "Institutions",
          field: "institutions_score",
          label: "Institutions",
          visible: false,
          color: "#FFC107",
        },
        {
          key: "Governance",
          field: "governance_score",
          label: "Governance",
          visible: false,
          color: "#FF9800",
        },
        {
          key: "Mobility",
          field: "mobility_score",
          label: "Mobility",
          visible: false,
          color: "#03A9F4",
        },
        {
          key: "DigitalInfrastructure",
          field: "digital_infrastructure_score",
          label: "Digital Infrastructure",
          visible: false,
          color: "#2196F3",
        },
        {
          key: "RDIntensity",
          field: "rd_intensity_score",
          label: "R&D Intensity",
          visible: false,
          color: "#673AB7",
        },
        {
          key: "IPIntensity",
          field: "ip_intensity_score",
          label: "Intellectual Property Intensity",
          visible: false,
          color: "#9C27B0",
        },
        {
          key: "FinancialActivity",
          field: "financial_activity_score",
          label: "Financial Activity",
          visible: false,
          color: "#E91E63",
        },
        {
          key: "CulturalVibrancy",
          field: "cultural_vibrancy_score",
          label: "Cultural Vibrancy",
          visible: false,
          color: "#F44336",
        },
        {
          key: "DataReadiness",
          field: "data_readiness_score",
          label: "Data Readiness",
          visible: false,
          color: "#4CAF50",
        },
        {
          key: "AIReadiness",
          field: "ai_readiness_score",
          label: "AI Readiness",
          visible: false,
          color: "#009688",
        },
      ],
    };
  },

  computed: {
    sortedCities() {
      return Object.values(this.citiesData).sort(
        (a, b) => a.future_readiness_rank - b.future_readiness_rank
      );
    },

    visibleColumns() {
      return [...this.mainPillars, ...this.subPillars].filter(
        (col) => col.visible
      );
    },

    chartData() {
      const sortedCities = this.sortedCities;

      return {
        chart: {
          height: (9 / 16) * 100 + "%",
        },
        labels: sortedCities.map((city) => city.city),
        datasets: this.visibleColumns.map((column) => ({
          label: column.label,
          data: sortedCities.map((city) => {
            const value = city[column.field];
            return value ? value.toFixed(2) : "0.00";
          }),
          backgroundColor: column.color,
        })),
      };
    },

    isMobile() {
      if (screen.width <= 760) {
        return true;
      } else {
        return false;
      }
    },
  },

  mounted() {
    const defaultPillars = [
      "CFRI",
      "PeopleCentricity",
      "Openness",
      "Sustainability",
      "Innovation",
      "EIntelligence",
    ];
    this.mainPillars.forEach((pillar) => {
      pillar.visible = defaultPillars.includes(pillar.key);
    });
    this.subPillars.forEach((subPillar) => (subPillar.visible = false));
  },
};
</script>

<style>
.graph-view-bar-chart-2025 {
  width: 100% !important;
  height: 10000px !important;
}

.help-text-green {
  color: #0f9d58;
}

@media (max-width: 768px) {
  .graph-view-bar-chart-2025 {
    height: 8000px !important;
  }
}
</style>
